import * as React from 'react';
import {
  EnvironmentFilled,
  RobotFilled,
  PhoneFilled,
  MessageFilled,
  FileSearchOutlined,
  ScheduleFilled,
  CloudOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { ThemeType } from '@src/theming/jssTypes';
import { Moment } from 'moment';
import {
  IAgentOptionConfigJson,
  IReportDataApiData,
  IReportMetric,
  IHeatmapPoint,
  IResolutionSlice,
  ISeriesPoint,
  IGetTasksParams,
  IGetCountsParams,
  GroupByKey,
  ISearchEmployeeBody,
  ITaskFilterMeta,
  ITaskFilterValues,
  IBuildTasksParamsOptions,
  IDashboardGroupTab,
  IDashboardReportRef,
  IObjectBinding,
  IGeofenceBinding,
  IObjectTypeOption,
  IGeofenceBoundObject,
  OBJECT_TYPE_CUSTOM,
} from './ControlTower.types';
import { getErrorMessage } from '../../../api/utils';

// Query-builder endpoints report failures as { status: false, error: '<message>' } —
// a plain string, unlike the app-wide { error: { message } } shape. Surface that string
// when present, else fall back to standard extraction. Pass into GET/POST as the
// error-message extractor so the real reason (e.g. an invalid objectId) reaches the UI.
export const getStringErrorMessage = (err): string => {
  const responseError = err?.response?.data?.error;
  return typeof responseError === 'string' ? responseError : getErrorMessage(err);
};

export const getAgentIcon = (icon?: string): React.ReactNode => {
  switch (icon) {
    case 'location':
      return React.createElement(EnvironmentFilled);
    case 'phone':
      return React.createElement(PhoneFilled);
    case 'message':
      return React.createElement(MessageFilled);
    case 'search':
      return React.createElement(FileSearchOutlined);
    case 'schedule':
      return React.createElement(ScheduleFilled);
    case 'cloud':
      return React.createElement(CloudOutlined);
    default:
      return React.createElement(RobotFilled);
  }
};

// `color` comes from the agent's config_json and is untrusted — it may be
// empty, a misspelling, or a value that isn't an accent token. Fall back to
// `blue` for any unknown key so the card renders instead of crashing on a
// missing accent object.
export const getAgentAccent = (theme: ThemeType, color?: string) => {
  const accent = theme.color.semantic.accent;
  return accent[color as keyof typeof accent] ?? accent.blue;
};

export const getPctColor = (pct: number, theme: ThemeType): string => {
  if (pct > 90) return theme.color.semantic.accent.green.strong;
  if (pct > 80) return theme.color.semantic.accent.orange.primary;
  return theme.color.semantic.accent.red.strong;
};

export const parseReportData = (data: IReportDataApiData): IReportMetric[] => {
  let chartData;
  try {
    chartData = JSON.parse(data.chartData);
  } catch {
    return [];
  }
  const row = chartData[0] ?? {};
  return data.metrices.map((m) => ({
    id: m._id,
    metric: m.metric,
    label: m.prettyName,
    value: row[m.metric] ?? 0,
    format: m.metric.startsWith('%') ? 'percent' as const : 'number' as const,
  }));
};

// Metrics report id: the `reports` entry of type 'metrics', else the legacy report_id.
export const getMetricsReportId = (config?: IAgentOptionConfigJson): string | undefined =>
  config?.reports?.find((r) => r.type === 'metrics')?.id ?? config?.report_id;

export const buildAgentReportFilters = (
  agentId: string,
  range?: [Moment, Moment] | null,
  hubCode?: string | null,
) => {
  const [from, to] = range ?? [];
  const filters = [
    {
      metric: 'createdAt',
      filter: {
        name: 'timestampzBetween',
        type: 'timestampz',
        data: JSON.stringify({
          value: { from: from?.format('YYYY-MM-DD'), to: to?.format('YYYY-MM-DD') },
        }),
      },
    },
    {
      metric: 'workflowId',
      filter: { name: 'textIsEqualTo', type: 'text', data: JSON.stringify({ value: [agentId] }) },
    },
  ];
  if (hubCode) {
    filters.push({
      metric: 'hubCode',
      filter: { name: 'textIsEqualTo', type: 'text', data: JSON.stringify({ value: [hubCode] }) },
    });
  }
  return filters;
};

const AGENT_GLOBAL_FILTER_META: Record<string, { filterId: string; prettyName: string; sql: string }> = {
  createdAt: {
    filterId: 'agentPlatformDbId.createdAt',
    prettyName: 'Created At',
    sql: 'job.created_at',
  },
  workflowId: {
    filterId: 'agentPlatformDbId.workflowId',
    prettyName: 'workflowId',
    sql: 'job.workflow_id',
  },
  hubCode: {
    filterId: 'agentPlatformDbId.hubCode',
    prettyName: 'Hub Code',
    sql: 'job.hub_code',
  },
};

// The backend binds a global filter to the report's own filter by filterId; without it
// the report's saved filter wins and the request is ignored.
export const buildAgentGlobalFilters = (
  agentId: string,
  range?: [Moment, Moment] | null,
  hubCode?: string | null,
  reportFilterIds?: Record<string, boolean>,
) =>
  buildAgentReportFilters(agentId, range, hubCode)
    .filter((f) => {
      const meta = AGENT_GLOBAL_FILTER_META[f.metric];
      if (!meta) return false;
      return !reportFilterIds || meta.filterId in reportFilterIds;
    })
    .map((f) => {
      const meta = AGENT_GLOBAL_FILTER_META[f.metric];
      return {
        metric: f.metric,
        filter: {
          ...f.filter,
          prettyName: meta.prettyName,
          isDefaultFilter: false,
          sql: meta.sql,
        },
        isCustomReportFilter: true,
        caseInsensitive: false,
        includeChildren: false,
        dashboardFilterType: 'simple',
        filterId: meta.filterId,
        filterPriority: 10,
        overrideOtherFilters: false,
      };
    });

export const parseDashboardGroup = (data: any): IDashboardGroupTab[] => {
  const dashboards = data?.dashboards;
  if (!Array.isArray(dashboards)) return [];
  return dashboards
    .map((d: any) => {
      const vis = d?.dashboardVisualisations;
      return {
        id: vis?.dashboard?._id ?? '',
        name: vis?.dashboard?.name ?? '',
        reports: (vis?.reports ?? []) as IDashboardReportRef[],
      };
    })
    .filter((tab) => tab.id);
};

// Heatmap report → one point per row: its `date` column + its numeric metric.
export const parseHeatmapData = (data: IReportDataApiData): IHeatmapPoint[] => {
  let rows: any[];
  try {
    rows = JSON.parse(data.chartData);
  } catch {
    return [];
  }
  if (!Array.isArray(rows)) return [];
  const dateMeta = data.metrices.find((m) => m.type === 'date');
  const countMeta = data.metrices.find((m) => m.type === 'number');
  if (!dateMeta || !countMeta) return [];
  return rows
    .map((r) => ({
      date: String(r[dateMeta.metric] ?? '').slice(0, 10),
      count: Number(r[countMeta.metric] ?? 0),
    }))
    .filter((p) => p.date);
};

// Resolution (pie) report → one slice per outcome. Handles three shapes:
//  A) pre-shaped visualisation rows: one slice per row, colorField/angleField (default x/y) name the keys.
//  B) manualQuery rows: one slice per row, a text column = label, a number column = value.
//  C) single row, one numeric column per outcome (values may be nested in `rawData`).
export const parseResolutionData = (data: IReportDataApiData): IResolutionSlice[] => {
  let rows: any[];
  try {
    rows = JSON.parse(data.chartData);
  } catch {
    return [];
  }
  if (!Array.isArray(rows) || !rows.length) return [];

  if (rows.length > 1) {
    const fd = parseFormData(data);
    const colorField = fd.colorField || 'x';
    const angleField = fd.angleField || 'y';
    // Shape A — pre-shaped rows expose the colour/angle keys directly.
    if (rows.some((r) => angleField in r)) {
      return rows
        .map((r) => ({
          label: String(r[colorField] ?? r.name ?? ''),
          value: Math.round(Number(r[angleField] ?? 0)),
        }))
        .filter((s) => s.label);
    }
    // Shape B — outcomes are row values of a text/label column.
    const labelMeta = data.metrices.find((m) => m.type === 'text');
    const pctMeta = data.metrices.find((m) => m.type === 'number' && /percent|%/i.test(m.metric));
    const valueMeta = data.metrices.find((m) => m.type === 'number' && m !== pctMeta);
    if (!labelMeta || !valueMeta) return [];
    return rows
      .map((r) => ({
        label: String(r[labelMeta.metric] ?? ''),
        value: Math.round(Number(r[valueMeta.metric] ?? 0)),
        percentage: pctMeta ? Number(r[pctMeta.metric] ?? 0) : undefined,
      }))
      .filter((s) => s.label);
  }

  // Shape C — outcomes are the numeric columns; keys may arrive line-wrapped or
  // nested under `rawData`, so read from rawData when present and match on collapsed whitespace.
  const row = rows[0] ?? {};
  const src = row.rawData && typeof row.rawData === 'object' ? row.rawData : row;
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
  const byKey = new Map(Object.keys(src).map((k) => [norm(k), src[k]]));
  return data.metrices
    .filter((m) => m.type === 'number')
    .map((m) => ({
      label: m.prettyName || m.metric,
      value: Math.round(Number(byKey.get(norm(m.metric)) ?? 0)),
    }));
};

// Raw chartData rows (for table / dualAxes / pre-shaped axis charts).
export const parseRows = (data: IReportDataApiData): Record<string, any>[] => {
  try {
    const rows = JSON.parse(data.chartData);
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
};

// Number report → KPI tiles. Rows are { value, title, metric }, one per measure.
export const parseNumbers = (data: IReportDataApiData): Array<{ label: string; value: number }> =>
  parseRows(data).map((r) => ({
    label: String(r.title ?? r.metric ?? ''),
    value: Number(r.value ?? 0),
  }));

// Visualisation `formData` (xField/yField/seriesField/isGroup/…) for axis charts.
export const parseFormData = (data: IReportDataApiData): Record<string, any> => {
  try {
    return data.formData ? JSON.parse(data.formData) : {};
  } catch {
    return {};
  }
};

// Axis charts (bar/line/progressBar) → plot-ready rows + field names. Two shapes:
//  A) many rows already plot-shaped → use as-is with formData's x/y/seriesField.
//  B) single row with values nested in `rawData` → expand each numeric column into
//     a per-outcome series row {x: category, y: value, series: outcome}.
export const parseAxisData = (
  data: IReportDataApiData,
): { rows: Record<string, any>[]; xField: string; yField: string; seriesField?: string } => {
  const fd = parseFormData(data);
  const raw = parseRows(data);
  const single = raw.length === 1 ? raw[0] : null;
  if (single && single.rawData && typeof single.rawData === 'object') {
    const category = String(single.x ?? single[fd.xField] ?? '');
    const rows = data.metrices
      .filter((m) => m.type === 'number')
      .map((m) => ({ x: category, y: Math.round(Number(single.rawData[m.metric] ?? 0)), series: m.prettyName || m.metric }));
    return { rows, xField: 'x', yField: 'y', seriesField: 'series' };
  }
  return { rows: raw, xField: fd.xField || 'x', yField: fd.yField || 'y', seriesField: fd.seriesField };
};

// Table report → antd columns. chartData rows are keyed by the display field/name
// (e.g. "Reference Number"), not the metric id, so dataIndex must match those keys.
// Column order + titles come from formData (columns/meta), falling back to row keys.
export const parseTableColumns = (
  data: IReportDataApiData,
): Array<{ title: string; dataIndex: string; key: string; width: number }> => {
  const fd = parseFormData(data);
  const rows = parseRows(data);
  const meta: any[] = Array.isArray(fd.meta) ? fd.meta : [];
  const keys: string[] = Array.isArray(fd.columns) && fd.columns.length
    ? fd.columns
    : rows.length && rows[0] && typeof rows[0] === 'object'
      ? Object.keys(rows[0]).filter((k) => k !== 'rawData')
      : [];
  return keys.map((key) => {
    const m = meta.find((x) => x && typeof x === 'object' && (x.field === key || x.name === key));
    const metric = data.metrices.find(
      (x) => x.prettyName === key || (x as any).aliasName === key || x.metric === key,
    );
    return {
      title: m?.name ?? metric?.prettyName ?? key,
      dataIndex: key,
      key,
      width: ((metric as any)?.columnWidth as number) ?? 150,
    };
  });
};

// Bar/line/area report → {x, y} points. Handles both shapes:
//  A) rows with an x (date/text) column + a y (number) column → one point per row.
//  B) single row, one numeric column per category → x = column label, y = its value.
export const parseSeriesData = (data: IReportDataApiData): ISeriesPoint[] => {
  let rows: any[];
  try {
    rows = JSON.parse(data.chartData);
  } catch {
    return [];
  }
  if (!Array.isArray(rows) || !rows.length) return [];

  const xMeta = data.metrices.find((m) => m.type === 'date' || m.type === 'text');
  const yMeta = data.metrices.find((m) => m.type === 'number');
  if (xMeta && yMeta) {
    return rows.map((r) => ({
      x: String(r[xMeta.metric] ?? ''),
      y: Number(r[yMeta.metric] ?? 0),
    }));
  }

  // Shape B — keys may arrive line-wrapped, so match on collapsed whitespace.
  const row = rows[0] ?? {};
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim();
  const byKey = new Map(Object.keys(row).map((k) => [norm(k), row[k]]));
  return data.metrices
    .filter((m) => m.type === 'number')
    .map((m) => ({ x: m.prettyName || m.metric, y: Number(byKey.get(norm(m.metric)) ?? 0) }));
};

// ─── API Param Builders ──────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 20;

const SEARCH_EMPLOYEE_QUERY = `query SEARCH_EMPLOYEE_BY_EMAIL($searchQuery: String) {
  searchEmplyoeeEmails(searchQuery: $searchQuery) {
    name
    email
    empId
    isAgent
  }
}`;

export const buildSearchEmployeeBody = (searchQuery = ''): ISearchEmployeeBody => ({
  operationName: 'SEARCH_EMPLOYEE_BY_EMAIL',
  variables: { searchQuery },
  query: SEARCH_EMPLOYEE_QUERY,
});

// Query-builder rejects ranges > 365 days, so unbounded presets are capped.
const DATE_RANGE_CAP_DAYS = 365;

// FE owns preset → { from, to } resolution for any filter with param_format
// 'date_range' (currently the SLA filter). Both bounds are required ISO strings.
export const mapDateRangePreset = (
  preset: string,
): { from: string; to: string } | null => {
  const now = moment();
  switch (preset) {
    case 'breached':
      return { from: now.clone().subtract(DATE_RANGE_CAP_DAYS, 'days').toISOString(), to: now.toISOString() };
    case 'sla_met':
      return { from: now.toISOString(), to: now.clone().add(DATE_RANGE_CAP_DAYS, 'days').toISOString() };
    case 'due_today':
      return { from: now.toISOString(), to: now.clone().endOf('day').toISOString() };
    case 'due_this_week':
      return { from: now.toISOString(), to: now.clone().endOf('week').toISOString() };
    case 'due_this_month':
      return { from: now.toISOString(), to: now.clone().endOf('month').toISOString() };
    default:
      return null;
  }
};

// FE owns the option → query-builder param mapping for any filter with
// param_format 'current_user_scope' (currently the Scope filter). The chosen
// option becomes the named param valued with the logged-in user's empId;
// 'all_tasks' (and anything unmapped) sends nothing — the default scope applies.
const CURRENT_USER_SCOPE_PARAMS: Record<string, string> = {
  assigned_to_me: 'assignee',
  supervising: 'taskSupervisor',
  created_by_me: 'createdBy',
  watching: 'selectedWatchers',
};

const isEmptyFilterValue = (v: any): boolean =>
  v === null
  || v === undefined
  || v === ''
  || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

// search/select widgets yield { key, label } objects; everything else is a scalar.
const scalarOf = (v: any): any =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v.key ?? v.value ?? v) : v;

// [startOfDay, endOfDay] ISO range for the selected created-at window.
const buildCreationTimeParam = (createdAt?: [Moment, Moment]): string | undefined =>
  createdAt
    ? JSON.stringify({
      from: createdAt[0].clone().startOf('day').toISOString(),
      to: createdAt[1].clone().endOf('day').toISOString(),
    })
    : undefined;

// Serialises each active filter under its catalogue `param` per its widget type.
// Shared by the list and the count endpoints — they accept the same filter set.
const applyCatalogueFilters = (
  target: Record<string, any>,
  filters: ITaskFilterValues,
  filterMetaById: Record<string, ITaskFilterMeta>,
  currentUserEmpId?: string,
): void => {
  const safeFilterMetaById = filterMetaById || {};

  Object.keys(filters).forEach((id) => {
    const value = filters[id];
    const meta = safeFilterMetaById[id];
    if (!meta || isEmptyFilterValue(value)) return;

    // Me-relative scope: the option maps to a param valued with the user's empId.
    if (meta.param_format === 'current_user_scope') {
      const scopeParam = CURRENT_USER_SCOPE_PARAMS[String(scalarOf(value))];
      if (scopeParam && currentUserEmpId) target[scopeParam] = JSON.stringify([currentUserEmpId]);
      return;
    }

    if (!meta.param) return;

    if (meta.param_format === 'date_range') {
      const range = mapDateRangePreset(String(scalarOf(value)));
      if (range) target[meta.param] = JSON.stringify(range);
      return;
    }
    switch (meta.type) {
      case 'search':
        target[meta.param] = meta.mode === 'multiple'
          ? JSON.stringify((Array.isArray(value) ? value : [value]).map(scalarOf))
          : scalarOf(value);
        break;
      case 'select':
        target[meta.param] = meta.mode === 'multiple'
          ? JSON.stringify(Array.isArray(value) ? value : [value])
          : scalarOf(value);
        break;
      case 'input':
        target[meta.param] = meta.mode === 'multiple'
          ? JSON.stringify((Array.isArray(value) ? value : [value]).map(scalarOf))
          : scalarOf(value);
        break;
      default:
        target[meta.param] = scalarOf(value);
        break;
    }
  });
};

// Group By dimension → the list/count query param its bucket `value` drills into.
// agent/assignee both key off the assignee filter (value is the empId); the rest
// reuse the same-named param.
const GROUP_DRILL_PARAM: Record<GroupByKey, string> = {
  agent: 'assignee',
  assignee: 'assignee',
  metricType: 'metricType',
  priority: 'priority',
  status: 'status',
};

export const applyGroupFilterToParams = (
  target: Record<string, any>,
  groupBy: GroupByKey,
  value: string,
): void => {
  const param = GROUP_DRILL_PARAM[groupBy];
  if (!param) return;
  // metricType is a scalar scope on the list endpoint (buildCountParams arrayifies
  // it for the count endpoints); every other dimension is a `$in` array on both.
  target[param] = param === 'metricType' ? value : JSON.stringify([value]);
};

// Builds the /incident-alert/ticket query params: fixed scope params plus each
// active filter serialised under its catalogue `param` according to its type.
export const buildGetTasksParams = (
  filters: ITaskFilterValues,
  filterMetaById: Record<string, ITaskFilterMeta>,
  options: IBuildTasksParamsOptions = {},
): IGetTasksParams => {
  const { createdAt, statusFilter, hubId, offSet, hitlParams, groupFilter, currentUserEmpId } = options;
  const params: IGetTasksParams = {
    metricType: 'Overall', // required scope; overridden by the metric_type filter when set
    pageSize: DEFAULT_PAGE_SIZE,
  };
  if (offSet) params.offSet = offSet;
  if (statusFilter?.length) params.status = JSON.stringify(statusFilter);
  if (hitlParams) {
    params.isHitlActionTaken = hitlParams.isHitlActionTaken;
    params.isHitlPresent = hitlParams.isHitlPresent;
  }
  if (hubId) params.hubId = JSON.stringify([hubId]);
  const creationTime = buildCreationTimeParam(createdAt);
  if (creationTime) params.creationTime = creationTime;

  const target = params as Record<string, any>;
  applyCatalogueFilters(target, filters, filterMetaById, currentUserEmpId);
  if (groupFilter) applyGroupFilterToParams(target, groupFilter.groupBy, groupFilter.value);

  return params;
};

// The list endpoint takes metricType as a scalar scope, but the count endpoints
// JSON.parse it as a `$in` filter — wrap the single-select value in an array so
// it parses. Leaves an already-serialised array (multi-select) untouched.
const arrayifyMetricType = (target: Record<string, any>): void => {
  const value = target.metricType;
  if (typeof value === 'string' && !value.trim().startsWith('[')) {
    target.metricType = JSON.stringify([value]);
  }
};

// Builds the shared filter scope for /status-counts and /grouped-counts: the same
// filter set as the list endpoint, minus pagination and the `metricType:'Overall'`
// scope default (these endpoints treat metricType as a plain `$in` filter).
export const buildCountParams = (
  filters: ITaskFilterValues,
  filterMetaById: Record<string, ITaskFilterMeta>,
  options: IBuildTasksParamsOptions = {},
): IGetCountsParams => {
  const { createdAt, statusFilter, hubId, hitlParams, groupFilter, currentUserEmpId } = options;
  const params: IGetCountsParams = {};
  const creationTime = buildCreationTimeParam(createdAt);
  if (creationTime) params.creationTime = creationTime;
  if (statusFilter?.length) params.status = JSON.stringify(statusFilter);
  if (hubId) params.hubId = JSON.stringify([hubId]);
  if (hitlParams) {
    params.isHitlActionTaken = hitlParams.isHitlActionTaken;
    params.isHitlPresent = hitlParams.isHitlPresent;
  }

  const target = params as Record<string, any>;
  applyCatalogueFilters(target, filters, filterMetaById, currentUserEmpId);
  if (groupFilter) applyGroupFilterToParams(target, groupFilter.groupBy, groupFilter.value);
  arrayifyMetricType(target);

  return params;
};

// ─── Object binding ───────────────────────────────────────────────────────────
// Older backends do not serve Custom in metadata.objectTypes.
export const withCustomObjectType = (
  objectTypes: IObjectTypeOption[] = [],
): IObjectTypeOption[] =>
  objectTypes.some((o) => o.key === OBJECT_TYPE_CUSTOM)
    ? objectTypes
    : [...objectTypes, { key: OBJECT_TYPE_CUSTOM, label: OBJECT_TYPE_CUSTOM }];

// The object type key a geofence binding pins the task type to. Resolved through the
// served geofence config rather than a hardcoded list, so a non-hub bound object works.
export const geofenceObjectTypeKey = (
  geofenceBinding: IGeofenceBinding | null | undefined,
  boundObjects: IGeofenceBoundObject[] = [],
  objectTypes: IObjectTypeOption[] = [],
): string | undefined => {
  if (!geofenceBinding?.enabled) {
    return undefined;
  }
  const boundObject = geofenceBinding.boundObject;
  if (!boundObject) {
    return undefined;
  }
  const rows = withCustomObjectType(objectTypes);
  const configured = boundObjects.find((o) => o.key === boundObject);
  const candidates = [boundObject, configured?.label].filter(Boolean) as string[];
  const match = rows.find((row) =>
    candidates.some(
      (candidate) =>
        row.key.toUpperCase() === candidate.toUpperCase() ||
        row.label.toUpperCase() === candidate.toUpperCase(),
    ),
  );
  return match?.key;
};

// Display label for an object type key, from the served options.
export const objectTypeLabel = (
  key: string | undefined,
  objectTypes: IObjectTypeOption[] = [],
): string => {
  if (!key) {
    return '';
  }
  const rows = withCustomObjectType(objectTypes);
  return rows.find((row) => row.key.toUpperCase() === key.toUpperCase())?.label || key;
};

// Every object type key a task of this type can be about, default first.
// Geofence overrides the binding. Task types saved before object binding existed
// resolve to [] so every type stays offered.
export const boundObjectTypes = (
  objectBinding: IObjectBinding | null | undefined,
  geofenceBinding: IGeofenceBinding | null | undefined,
  boundObjects: IGeofenceBoundObject[] = [],
  objectTypes: IObjectTypeOption[] = [],
): string[] => {
  if (geofenceBinding?.enabled) {
    const key = geofenceObjectTypeKey(geofenceBinding, boundObjects, objectTypes);
    return key ? [key] : [];
  }
  if (!objectBinding?.defaultObjectType) {
    return [];
  }
  return Array.from(
    new Set(
      [
        objectBinding.defaultObjectType,
        ...(objectBinding.additionalObjectTypes ?? []),
      ].filter(Boolean),
    ),
  );
};

// The binding a geofence-bound task type implies. HLD 2.2: legacy task types carrying
// only geofenceBinding read as bound to the geofence object, without the admin
// having to toggle geofence off and on.
export const bindingForGeofence = (
  objectBinding: IObjectBinding | null | undefined,
  geofenceBinding: IGeofenceBinding | null | undefined,
  boundObjects: IGeofenceBoundObject[] = [],
  objectTypes: IObjectTypeOption[] = [],
): IObjectBinding | null => {
  const key = geofenceObjectTypeKey(geofenceBinding, boundObjects, objectTypes);
  if (!key) {
    return objectBinding ?? null;
  }
  return {
    defaultObjectType: key,
    additionalObjectTypes: [],
    validateObjectCode: true,
  };
};
